import React, { useState, useEffect, useRef, useCallback } from 'react';
import { BrowserRouter, Routes, Route, Link, NavLink, useParams, useLocation, useNavigate } from 'react-router-dom';
import toast, { Toaster } from 'react-hot-toast';
import FilterBar from './components/FilterBar';
import ProductGrid from './components/ProductGrid';
import ProductModal from './components/ProductModal';
import CartModal from './components/CartModal';
import ChatBot from './components/ChatBot';
import ArticleModal from './components/ArticleModal';
import CertsSection from './components/CertsSection';
import DeliveryPage from './components/DeliveryPage';
import articles from './data/articles.json';
import './index.css';
import PrivacyPage from './components/PrivacyPage';
import OfferPage from './components/OfferPage';
import CookieBanner from './components/CookieBanner';
import AdminPage from './components/AdminPage';
import CatalogPage from './components/CatalogPage';
import SearchPage from './components/SearchPage';
// НОВОЕ: импорт ID бестселлеров из единого источника метаданных
import { BESTSELLER_IDS, priceInfo, promoTimeLeft, productVolume } from './productMeta';
import { fetchProducts, fetchActiveCartPromotions } from './api';
import PromoTopBar from './components/PromoTopBar';
import PromoSection from './components/PromoSection';
import { SupportProvider } from './context/SupportContext';
import SupportChat from './components/SupportChat';
import { AuthProvider, useAuth } from './context/AuthContext';
import AuthPage from './components/AuthPage';
import AccountPage from './components/AccountPage';
import WishlistPage from './components/WishlistPage';

/* ---------- Scroll to top on route change ---------- */
function ScrollToTop() {
    const { pathname, hash } = useLocation();
    useEffect(() => {
        if (hash) {
            // Если в ссылке есть #якорь, скроллим к нему
            const el = document.getElementById(hash.substring(1));
            if (el) {
                el.scrollIntoView({ behavior: 'smooth' });
                return;
            }
        }
        // Иначе скроллим в самый верх
        window.scrollTo(0, 0);
    }, [pathname, hash]);
    return null;
}

/* ---------- Кастомный toast добавления в корзину ---------- */
function AddedToCartToast({ t, product, onGoToCart }) {
    const photo = product.photo || 'https://placehold.co/96x96/F5F0EB/6B7A6A?text=PS';
    return (
        <div
            className={`${t.visible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-2'
                } transition-all duration-200 max-w-[380px] w-[calc(100vw-32px)] sm:w-auto bg-white rounded-2xl shadow-2xl border border-[#E8E3DD] p-3 flex items-center gap-3 pointer-events-auto`}
        >
            <img
                src={photo}
                alt=""
                className="w-14 h-14 rounded-xl object-cover bg-[#F5F0EB] shrink-0"
            />
            <div className="flex-1 min-w-0">
                <p className="text-[11px] uppercase tracking-widest text-[#6B7A6A] font-semibold mb-0.5">
                    ✓ Добавлено в корзину
                </p>
                <p className="text-sm font-medium text-[#1C1917] line-clamp-2 leading-tight">
                    {product.name}
                </p>
            </div>
            <button
                onClick={onGoToCart}
                className="shrink-0 text-xs font-semibold text-white bg-[#6B7A6A] hover:bg-[#5a6a59] rounded-full px-3 py-1.5 transition"
            >
                В корзину
            </button>
        </div>
    );
}

/* ---------- SEO-страница статьи журнала ---------- */
function ArticlePage() {
    const { slug } = useParams();
    const article = articles.find((a) => a.slug === slug);
    useEffect(() => {
        if (article) document.title = `${article.title} — журнал Pure Synergy`;
    }, [article]);
    if (!article) {
        return (
            <div className="text-center py-20">
                <p className="serif text-2xl mb-4">Статья не найдена</p>
                <Link to="/journal" className="text-[#6B7A6A] underline">← К журналу</Link>
            </div>
        );
    }
    return (
        <article className="max-w-3xl mx-auto py-12 px-4">
            <Link to="/journal" className="text-sm text-[#6B7A6A] hover:underline">← Журнал</Link>
            <p className="text-xs text-[#6B7A6A] font-semibold mt-6 uppercase tracking-widest">{article.category}</p>
            <h1 className="serif text-3xl md:text-4xl text-[#1C1917] mt-2 mb-3">{article.title}</h1>
            <p className="text-sm text-gray-500 mb-8">{article.readTime}</p>
            <div className={`h-56 rounded-3xl mb-8 ${article.cover}`}></div>
            <div className="space-y-5 text-gray-700 leading-relaxed">
                {article.content.map((b, i) => {
                    if (b.t === 'h3') return <h3 key={i} className="serif text-2xl text-[#1C1917] pt-4">{b.x}</h3>;
                    if (b.t === 'ul') return <ul key={i} className="list-disc pl-6 space-y-2">{b.x.map((li, j) => <li key={j}>{li}</li>)}</ul>;
                    return <p key={i}>{b.x}</p>;
                })}
            </div>
            <div className="mt-10 bg-[#F5F0EB] rounded-2xl p-6 text-center">
                <p className="font-semibold mb-3">Подберём уход под вашу кожу?</p>
                <button
                    onClick={() => window.dispatchEvent(new CustomEvent('open-chatbot'))}
                    className="btn-primary"
                >
                    Спросить AI-консультанта
                </button>
            </div>
        </article>
    );
}

/* ---------- SEO-страница товара ---------- */
function ProductPage({ products, addToCart }) {
    const { id } = useParams();
    const product = products.find((p) => String(p.id) === String(id));
    useEffect(() => {
        if (product) document.title = `${product.name} — купить | Pure Synergy`;
    }, [product]);
    if (!products.length) {
        return <div className="text-center py-20 text-gray-500">Загрузка…</div>;
    }
    if (!product) {
        return (
            <div className="text-center py-20">
                <p className="serif text-2xl mb-4">Товар не найден</p>
                <Link to="/" className="text-[#6B7A6A] underline">← В каталог</Link>
            </div>
        );
    }
    const photo = product.photo || 'https://placehold.co/600x600/F5F0EB/6B7A6A?text=Pure+Synergy';
    const volume = productVolume(product);

    // НОВОЕ: расчёт акционной цены
    const pi = priceInfo(product);
    const hasDiscount = pi.discount_amount > 0;

    return (
        <div className="max-w-5xl mx-auto py-12 px-4">
            <Link to="/#catalog-section" className="text-sm text-[#6B7A6A] hover:underline">← Каталог</Link>
            <div className="grid md:grid-cols-2 gap-10 mt-6">
                <img
                    src={photo}
                    alt={product.name}
                    className="rounded-3xl w-full aspect-[4/5] object-cover bg-[#F5F0EB]"
                />
                <div>
                    <p className="text-xs uppercase tracking-widest text-[#6B7A6A] font-semibold">{product.brand || ''}</p>
                    <h1 className="serif text-3xl text-[#1C1917] mt-2 mb-4">{product.name}</h1>
                    <p className="text-sm text-gray-500 mt-1 mb-4">
                        {product.category}{volume ? ` · ${volume}` : ''}
                    </p>

                    {/* НОВОЕ: блок цены с акцией */}
                    {hasDiscount ? (
                        <div className="mb-6">
                            <div className="flex items-baseline gap-3 flex-wrap">
                                <span className="text-3xl font-bold text-red-600">
                                    {Math.round(pi.final_price)} ₽
                                </span>
                                <span className="text-lg text-gray-400 line-through">
                                    {Math.round(pi.base_price)} ₽
                                </span>
                                {pi.badge && (
                                    <span className="bg-red-500 text-white text-sm font-bold px-3 py-1 rounded-full">
                                        {pi.badge}
                                    </span>
                                )}
                            </div>
                            <p className="text-sm text-[#6B7A6A] mt-2">
                                Вы экономите {Math.round(pi.discount_amount)} ₽
                                {pi.ends_at && <> · {promoTimeLeft(pi.ends_at)}</>}
                            </p>
                        </div>
                    ) : (
                        <p className="text-2xl font-bold text-[#6B7A6A] mb-6">{Math.round(product.price)} ₽</p>
                    )}

                    <p className="text-gray-700 leading-relaxed mb-8 whitespace-pre-line">{product.description}</p>
                    <div className="flex flex-wrap gap-3">
                        <button onClick={() => addToCart(product)} className="btn-primary">В корзину</button>
                        <button
                            onClick={() => window.dispatchEvent(new CustomEvent('open-chatbot'))}
                            className="btn-outline"
                        >
                            Задать вопрос
                        </button>
                    </div>
                    <p className="text-xs text-gray-500 mt-6">
                        ✅ Сертифицировано · <Link to="/certs" className="underline text-[#6B7A6A]">декларации ЕАС и Халяль</Link>
                    </p>
                </div>
            </div>
        </div>
    );
}

/* ---------- Кнопка / меню авторизации в хедере ---------- */
function AuthHeaderButton() {
    const { user, loading, configEnabled, logout } = useAuth();
    const [menuOpen, setMenuOpen] = React.useState(false);
    const navigate = useNavigate();
    const ref = React.useRef(null);

    React.useEffect(() => {
        const onDoc = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setMenuOpen(false);
        };
        document.addEventListener('mousedown', onDoc);
        return () => document.removeEventListener('mousedown', onDoc);
    }, []);

    // Фича выключена или идёт первичная проверка — кнопку не показываем
    if (!configEnabled || loading) return null;

    if (!user) {
        return (
            <button
                onClick={() => navigate('/account/login')}
                className="text-[#1C1917] hover:text-[#6B7A6A] transition"
                aria-label="Войти"
                title="Войти"
            >
                <i className="far fa-user text-lg"></i>
            </button>
        );
    }

    return (
        <div className="relative" ref={ref}>
            <button
                onClick={() => setMenuOpen((v) => !v)}
                className="text-[#1C1917] hover:text-[#6B7A6A] transition flex items-center"
                aria-label="Профиль"
                title={user.email}
            >
                <i className="fas fa-user-circle text-lg"></i>
            </button>
            {menuOpen && (
                <div className="absolute right-0 top-full mt-2 bg-white border border-[#E8E3DD] rounded-lg shadow-xl z-[60] min-w-[200px] py-1">
                    <div className="px-3 py-2 text-xs text-gray-500 border-b border-[#F0ECE8] truncate">
                        {user.email}
                    </div>
                    <Link
                        to="/account"
                        onClick={() => setMenuOpen(false)}
                        className="block px-3 py-2 text-sm hover:bg-[#F5F0EB] transition"
                    >
                        Личный кабинет
                    </Link>
                    <button
                        onClick={async () => {
                            setMenuOpen(false);
                            await logout();
                            navigate('/');
                        }}
                        className="block w-full text-left px-3 py-2 text-sm text-red-500 hover:bg-[#F5F0EB] transition"
                    >
                        Выйти
                    </button>
                </div>
            )}
        </div>
    );
}

/* ---------- Основной Shell ---------- */
function Shell() {
    const [products, setProducts] = useState([]);
    const [cartPromos, setCartPromos] = useState([]);
    const [catalogDown, setCatalogDown] = useState(false);
    const [cart, setCart] = useState(() => JSON.parse(localStorage.getItem('cart')) || []);
    const { user, isAuthenticated } = useAuth();
    const [wishlistIds, setWishlistIds] = useState(() => new Set());
    const wishlistSyncedRef = useRef(null);
    const [bonusBalance, setBonusBalance] = useState(0);
    const bonusSyncedRef = useRef(null);
    const syncedUserRef = useRef(null);       // какой user_id уже слит с сервером
    const syncTimerRef = useRef(null);        // debounce-таймер для PUT
    const [cartOpen, setCartOpen] = useState(false);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [userId] = useState(() => localStorage.getItem('userId') || 'user_' + Date.now());
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [selectedArticle, setSelectedArticle] = useState(null);
    const [payResult, setPayResult] = useState(() => {
        const p = window.location.pathname;
        if (p.includes('payment-success')) return 'success';
        if (p.includes('payment-fail')) return 'fail';
        return null;
    });
    // Отправляем цель purchase с суммой из localStorage — её сохранил CartModal
    useEffect(() => {
        if (payResult !== 'success') return;
        if (typeof window.ym !== 'function') return;

        let order = null;
        try {
            const raw = localStorage.getItem('last_order');
            if (raw) {
                const parsed = JSON.parse(raw);
                // заказ не старше 1 часа
                if (parsed && Date.now() - (parsed.created_at || 0) < 3600_000) {
                    order = parsed;
                }
            }
        } catch (_) { }

        if (order) {
            window.ym(112121340, 'reachGoal', 'purchase', {
                order_price: Number(order.amount_rub || 0),
                currency: 'RUB',
                order_id: order.order_id || '',
            });
            // Чистим, чтобы не дублировать при перезагрузке
            try { localStorage.removeItem('last_order'); } catch (_) { }
        } else {
            // fallback — просто цель без суммы
            window.ym(112121340, 'reachGoal', 'purchase_visit');
        }
    }, [payResult]);

    useEffect(() => {
        if (!payResult) return;
        window.history.replaceState({}, '', '/');
        const timer = setTimeout(() => setPayResult(null), 10000);
        return () => clearTimeout(timer);
    }, [payResult]);

    const [searchQuery, setSearchQuery] = useState('');
    const [isSearchOpen, setIsSearchOpen] = useState(false);
    const searchInputRef = useRef(null);

    // НОВОЕ: тень у хедера при скролле — визуальный сигнал «шапка прилипла»
    const [scrolled, setScrolled] = useState(false);
    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 8);
        window.addEventListener('scroll', onScroll, { passive: true });
        onScroll();
        return () => window.removeEventListener('scroll', onScroll);
    }, []);

    const navigate = useNavigate();
    const location = useLocation();

    const playlist = ['/video1.mp4', '/video2.mp4'];
    const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
    const handleVideoEnd = () => {
        setCurrentVideoIndex((prev) => (prev + 1) % playlist.length);
    };

    const catalogRef = useRef(null);
    const scrollToCatalog = () => {
        if (catalogRef.current) {
            catalogRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };

    // Открыть корзину (используется из toast и из кликов)
    const openCart = useCallback(() => {
        setCartOpen(true);
        toast.dismiss();
    }, []);

    // Добавление в корзину + toast
    const addToCart = useCallback((product) => {
        // НОВОЕ: сохраняем финальную цену с учётом акции
        const finalPrice = Number(product?.price_info?.final_price ?? product.price ?? 0);
        const cartItem = { ...product, price: finalPrice };

        setCart(prev => {
            const existing = prev.find(item => item.id === product.id);
            if (existing) {
                return prev.map(item =>
                    item.id === product.id ? { ...item, qty: item.qty + 1 } : item
                );
            }
            return [...prev, { ...cartItem, qty: 1 }];
        });
        if (window.ym) window.ym(112121340, 'reachGoal', 'add_to_cart');

        // Toast с фото и кнопкой «В корзину»
        toast.custom(
            (t) => (
                <AddedToCartToast
                    t={t}
                    product={product}
                    onGoToCart={openCart}
                />
            ),
            {
                duration: 3500,
                position: 'top-center',
            }
        );
    }, [openCart]);

    // Удаление из корзины
    const removeFromCart = useCallback((id) => {
        setCart(prev => prev.filter(item => item.id !== id));
    }, []);

    // Изменение количества товара (для кнопок +/− в корзине)
    const updateQuantity = useCallback((id, qty) => {
        if (qty <= 0) {
            setCart(prev => prev.filter(item => item.id !== id));
            return;
        }
        setCart(prev => prev.map(item =>
            item.id === id ? { ...item, qty } : item
        ));
    }, []);

    useEffect(() => {
        window.__addToCart = addToCart;
        window.__openProductModal = (product) => {
            if (typeof product === 'string') {
                const found = products.find(p => p.id === product);
                if (found) setSelectedProduct(found);
            } else {
                setSelectedProduct(product);
            }
        };
    }, [addToCart, products]);

    useEffect(() => {
        fetchProducts()
            .then(data => {
                console.log('Загружено товаров:', data.length);
                setProducts(data);
                setCatalogDown(!data || data.length === 0);
            })
            .catch(err => {
                console.error('Ошибка загрузки:', err);
                setCatalogDown(true);
            });

        fetchActiveCartPromotions()
            .then((promos) => setCartPromos(promos || []))
            .catch(() => setCartPromos([]));
    }, []);

    // ─── Если каталог пуст — периодически проверяем, не вернулся ли он ───
    useEffect(() => {
        if (!catalogDown) return;
        const timer = setInterval(async () => {
            try {
                const data = await fetchProducts();
                if (data && data.length > 0) {
                    setProducts(data);
                    setCatalogDown(false);
                }
            } catch (_) { }
        }, 60000);
        return () => clearInterval(timer);
    }, [catalogDown]);

    useEffect(() => {
        localStorage.setItem('cart', JSON.stringify(cart));
    }, [cart]);

    // ─── НОВОЕ: слияние локальной и серверной корзины при логине ───
    useEffect(() => {
        // Не авторизован — сброс, чтобы при следующем входе слияние произошло заново
        if (!isAuthenticated || !user?.user_id) {
            syncedUserRef.current = null;
            return;
        }
        // Уже сливали с этим пользователем — не повторяем
        if (syncedUserRef.current === user.user_id) return;
        // Ждём загрузки каталога, чтобы иметь полные объекты товаров
        if (!products.length) return;

        let stop = false;
        (async () => {
            try {
                // 1. Загружаем серверную корзину
                const r = await fetch('/api/account/cart', { credentials: 'include' });
                if (!r.ok) return;
                const serverData = await r.json();
                const serverItems = new Map(
                    (serverData.items || []).map((it) => [String(it.id), it.qty])
                );

                // 2. Локальная корзина
                const localItems = new Map(
                    (cart || []).map((it) => [String(it.id), it.qty])
                );

                // 3. Слияние: по каждому product_id берём МАКСИМУМ, а не сумму
                const allIds = new Set([...serverItems.keys(), ...localItems.keys()]);
                const mergedItems = [];
                for (const id of allIds) {
                    const qty = Math.max(serverItems.get(id) || 0, localItems.get(id) || 0);
                    if (qty > 0) mergedItems.push({ id, qty });
                }

                if (stop) return;

                // 4. Собираем полные объекты товаров для локальной корзины
                const productById = new Map(products.map((p) => [String(p.id), p]));
                const newCart = [];
                for (const { id, qty } of mergedItems) {
                    const product = productById.get(id);
                    if (!product) continue;   // товара больше нет в каталоге — пропускаем
                    const finalPrice = Number(
                        product?.price_info?.final_price ?? product.price ?? 0
                    );
                    newCart.push({ ...product, price: finalPrice, qty });
                }
                setCart(newCart);

                // 5. Отправляем объединённую корзину на сервер
                await fetch('/api/account/cart', {
                    method: 'PUT',
                    credentials: 'include',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ items: mergedItems }),
                });

                syncedUserRef.current = user.user_id;
                console.log('[Cart] merged with server:', mergedItems);
            } catch (e) {
                console.warn('[Cart] sync failed:', e);
            }
        })();

        return () => { stop = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAuthenticated, user?.user_id, products]);   // НЕ включаем cart — иначе бесконечный цикл

    // ─── НОВОЕ: слияние избранного при логине ───
    useEffect(() => {
        if (!isAuthenticated || !user?.user_id) {
            wishlistSyncedRef.current = null;
            return;
        }
        if (wishlistSyncedRef.current === user.user_id) return;
        if (!products.length) return;

        let stop = false;
        (async () => {
            try {
                const r = await fetch('/api/account/wishlist', { credentials: 'include' });
                if (!r.ok) return;
                const data = await r.json();
                const serverIds = new Set((data.product_ids || []).map(String));
                if (stop) return;
                setWishlistIds(serverIds);
                wishlistSyncedRef.current = user.user_id;
                console.log('[Wishlist] loaded from server:', serverIds.size);
            } catch (e) {
                console.warn('[Wishlist] load failed:', e);
            }
        })();

        return () => { stop = true; };
    }, [isAuthenticated, user?.user_id, products.length]);

    // ─── НОВОЕ: переключатель избранного (с защитой от race condition) ───
    const toggleFav = useCallback(async (product) => {
        if (!product?.id) return;
        const pid = String(product.id);

        // Для анонимных — только приглашение, state не трогаем
        if (!isAuthenticated) {
            toast('Войдите, чтобы сохранить избранное', { icon: '❤️' });
            return;
        }

        // Ключевой фикс: вычисляем новый Set и отправляем на сервер
        // ВНУТРИ setState(prev => ...) — там всегда самое актуальное состояние.
        setWishlistIds((prev) => {
            const next = new Set(prev);
            if (next.has(pid)) next.delete(pid);
            else next.add(pid);

            // Отправляем на сервер именно тот next, который только что получили.
            // Даже если клики летят одновременно — каждый вызов prev уже включает
            // результат предыдущего клика, потому что React последовательно
            // применяет функциональные setState.
            const arr = Array.from(next);
            fetch('/api/account/wishlist', {
                method: 'PUT',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ product_ids: arr }),
            }).catch((e) => console.warn('[Wishlist] save failed:', e));

            return next;
        });
    }, [isAuthenticated]);   // ← больше НЕ зависит от wishlistIds — функция стабильная

    // ─── НОВОЕ: загрузка баланса бонусов при логине ───
    useEffect(() => {
        if (!isAuthenticated || !user?.user_id) {
            bonusSyncedRef.current = null;
            setBonusBalance(0);
            return;
        }
        if (bonusSyncedRef.current === user.user_id) return;

        let stop = false;
        (async () => {
            try {
                const r = await fetch('/api/account/bonus', { credentials: 'include' });
                if (!r.ok) return;
                const d = await r.json();
                if (stop) return;
                setBonusBalance(Number(d.balance) || 0);
                bonusSyncedRef.current = user.user_id;
                console.log('[Bonus] loaded:', d.balance);
            } catch (e) {
                console.warn('[Bonus] load failed:', e);
            }
        })();

        return () => { stop = true; };
    }, [isAuthenticated, user?.user_id]);

    // ─── НОВОЕ: debounced PUT на сервер при изменениях корзины ───
    useEffect(() => {
        // Пока не авторизован или ещё не выполнено первое слияние — не шлём
        if (!isAuthenticated || !user?.user_id) return;
        if (syncedUserRef.current !== user.user_id) return;

        if (syncTimerRef.current) clearTimeout(syncTimerRef.current);
        syncTimerRef.current = setTimeout(() => {
            const items = (cart || []).map((it) => ({
                id: String(it.id),
                qty: Number(it.qty) || 1,
            }));
            fetch('/api/account/cart', {
                method: 'PUT',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ items }),
            }).catch((e) => console.warn('[Cart] PUT failed:', e));
        }, 500);

        return () => {
            if (syncTimerRef.current) clearTimeout(syncTimerRef.current);
        };
    }, [cart, isAuthenticated, user?.user_id]);
    // НОВОЕ: синхронизация цен в корзине с актуальными данными из API.
    // Срабатывает при загрузке товаров, при активации/деактивации акций,
    // а также убирает из корзины товары, которых больше нет в каталоге.
    useEffect(() => {
        if (!products.length) return;
        setCart(prev => {
            if (!prev.length) return prev;
            let changed = false;
            const next = prev.map(item => {
                const fresh = products.find(p => String(p.id) === String(item.id));
                if (!fresh) return item;
                const freshPrice = Number(fresh?.price_info?.final_price ?? fresh.price ?? 0);
                if (Math.abs((item.price || 0) - freshPrice) > 0.01) {
                    changed = true;
                    return { ...fresh, qty: item.qty, price: freshPrice };
                }
                return item;
            });
            return changed ? next : prev;
        });
    }, [products]);

    // Сброс title на дефолтный для страниц, которые его не устанавливают сами
    useEffect(() => {
        const path = location.pathname;
        const setIf = (cond, title) => { if (cond) document.title = title; };
        setIf(path === '/', 'Pure Synergy — профессиональная космецевтика: купить с доставкой по России');
        setIf(path === '/skincare', 'Уход за кожей — протоколы по типам | Pure Synergy');
        setIf(path === '/about', 'О компании Pure Synergy — российская космецевтика');
        setIf(path === '/journal', 'Журнал Pure Synergy — статьи об уходе за кожей');
        setIf(path === '/certs', 'Сертификаты и декларации ЕАС | Pure Synergy');
        setIf(path === '/delivery', 'Оплата и доставка | Pure Synergy');
        setIf(path === '/privacy', 'Политика обработки персональных данных | Pure Synergy');
        setIf(path === '/offer', 'Публичная оферта | Pure Synergy');
        // НОВОЕ: title для страницы бестселлеров
        setIf(path === '/bestsellers', 'Бестселлеры Pure Synergy — популярные средства для кожи');
    }, [location.pathname]);

    const totalItems = cart.reduce((sum, item) => sum + item.qty, 0);
    const totalPrice = cart.reduce((sum, item) => sum + item.price * item.qty, 0);

    const displayProducts = products.length ? products : [];
    // НОВОЕ: бестселлеры — фильтруем по ID из productMeta.js (реальные продажи)
    const bestSellers = displayProducts.filter((p) =>
        BESTSELLER_IDS.has(String(p.id))
    );

    const q = String(searchQuery || '').trim().toLowerCase();
    const filteredProducts = q
        ? displayProducts.filter(p => String(p.name || '').toLowerCase().includes(q))
        : displayProducts;

    const handleSearchSubmit = (e) => {
        e?.preventDefault?.();
        const query = searchQuery.trim();
        setIsSearchOpen(false);
        if (query) {
            navigate(`/search?q=${encodeURIComponent(query)}`);
            setSearchQuery('');
        }
    };
    const handleModalClose = () => setSelectedProduct(null);
    const openChatBot = () => {
        window.dispatchEvent(new CustomEvent('open-chatbot'));
    };

    // Клик по товару в каталоге — SPA-переход на SEO-страницу товара
    const handleProductClick = (p) => {
        if (p && p.id) {
            navigate(`/product/${p.id}`);
        }
    };

    // Навигация
    const NAV = [
        { to: '/', label: 'Каталог', end: true },
        { to: '/bestsellers', label: 'Бестселлеры', end: false },
        { to: '/skincare', label: 'Уход за кожей', end: false },
        { to: '/about', label: 'О нас', end: false },
        { to: '/journal', label: 'Журнал', end: false },
    ];

    // Получаем clientID Метрики: сначала из cookie, потом через API счётчика
    const [metrikaId, setMetrikaId] = useState(() => {
        try {
            const m = document.cookie.match(/(?:^|;\s*)_ym_uid=([^;]+)/);
            return m ? decodeURIComponent(m[1]) : null;
        } catch { return null; }
    });

    useEffect(() => {
        if (metrikaId) return;
        if (typeof window.ym === 'function') {
            window.ym(112121340, 'getClientID', (id) => {
                if (id) setMetrikaId(String(id));
            });
        }
    }, [metrikaId]);

    return (
        <div className="min-h-screen bg-[#FBF9F6] flex flex-col">
            <ScrollToTop />
            <PromoTopBar promotions={cartPromos} />
            {/* Toast-контейнер: top-center, стилизация под бренд */}
            <Toaster
                position="top-center"
                toastOptions={{
                    duration: 3500,
                    style: {
                        background: 'transparent',
                        boxShadow: 'none',
                        padding: 0,
                        margin: 0,
                        maxWidth: 'none',
                    },
                }}
                containerStyle={{
                    top: 80,
                }}
            />
            {/* HEADER */}
            <header
                className={`bg-white/80 backdrop-blur-sm border-b sticky top-0 z-50 transition-shadow duration-200 ${scrolled
                        ? 'border-[#E8E3DD] shadow-[0_4px_16px_rgba(0,0,0,0.06)]'
                        : 'border-transparent'
                    }`}
            >
                <div className={`container mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between transition-all duration-200 ${scrolled ? 'py-2.5' : 'py-4'
                    }`}>
                    <Link to="/" className="flex items-baseline gap-1">
                        <span className="serif text-2xl tracking-wide">Pure<span className="text-[#6B7A6A]">Synergy</span></span>
                        <span className="text-[10px] tracking-[0.15em] uppercase text-gray-400 ml-1 hidden sm:inline">премиум косметика</span>
                    </Link>
                    <nav className="hidden md:flex items-center gap-8 text-sm font-medium text-[#1C1917]">
                        {NAV.map((n) => (
                            <NavLink
                                key={n.to}
                                to={n.to}
                                end={n.end}
                                className={({ isActive }) => `hover:text-[#6B7A6A] transition ${isActive ? 'text-[#6B7A6A]' : ''}`}
                            >
                                {n.label}
                            </NavLink>
                        ))}
                    </nav>
                    <div className="flex items-center gap-5">
                        <button
                            onClick={() => {
                                setIsSearchOpen(!isSearchOpen);
                                if (!isSearchOpen) setTimeout(() => searchInputRef.current?.focus(), 100);
                            }}
                            className="text-[#1C1917] hover:text-[#6B7A6A] transition"
                            aria-label="Поиск"
                        >
                            <i className="fas fa-search text-lg"></i>
                        </button>
                        <AuthHeaderButton />
                        {isAuthenticated && bonusBalance > 0 && (
                            <Link
                                to="/account"
                                title={`Бонусов: ${bonusBalance} ₽`}
                                className="hidden sm:flex items-center gap-1 text-xs font-semibold text-[#6B7A6A] border border-[#6B7A6A] rounded-full px-2 py-0.5 hover:bg-[#6B7A6A] hover:text-white transition"
                            >
                                💎 {bonusBalance}
                            </Link>
                        )}
                        <button
                            onClick={() => {
                                if (!isAuthenticated) {
                                    navigate('/account/login');
                                    return;
                                }
                                navigate('/account/wishlist');
                            }}
                            className="relative text-[#1C1917] hover:text-[#6B7A6A] transition"
                            aria-label="Избранное"
                            title="Избранное"
                        >
                            <i className={wishlistIds.size > 0 ? 'fas fa-heart text-lg text-red-500' : 'far fa-heart text-lg'}></i>
                            {wishlistIds.size > 0 && (
                                <span className="absolute -top-2 -right-3 bg-red-500 text-white text-[10px] w-5 h-5 rounded-full flex items-center justify-center">
                                    {wishlistIds.size}
                                </span>
                            )}
                        </button>
                        <button onClick={() => setCartOpen(true)} className="relative text-[#1C1917] hover:text-[#6B7A6A] transition">
                            <i className="fas fa-shopping-bag text-lg"></i>
                            {totalItems > 0 && (
                                <span className="absolute -top-2 -right-3 bg-[#6B7A6A] text-white text-[10px] w-5 h-5 rounded-full flex items-center justify-center">
                                    {totalItems}
                                </span>
                            )}
                        </button>
                        {/* НОВОЕ: индикатор активной акции */}
                        {cartPromos.length > 0 && (
                            <button
                                onClick={() => setCartOpen(true)}
                                title={cartPromos[0].short_text}
                                className="hidden sm:flex items-center gap-1 text-[10px] font-semibold text-[#6B7A6A] border border-[#6B7A6A] rounded-full px-2 py-0.5 hover:bg-[#6B7A6A] hover:text-white transition"
                            >
                                <i className="fas fa-percent text-[8px]"></i>
                                <span>акция</span>
                            </button>
                        )}
                        <button
                            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                            className="md:hidden text-[#1C1917] hover:text-[#6B7A6A] transition"
                            aria-label="Меню"
                        >
                            <i className={`fas ${mobileMenuOpen ? 'fa-times' : 'fa-bars'} text-xl`}></i>
                        </button>
                    </div>
                </div>

                {/* Поиск — вынесен из шапки, чтобы на мобильном не вылезала */}
                {isSearchOpen && (
                    <div
                        className="absolute left-3 right-3 sm:left-auto sm:right-6 top-full mt-2 sm:w-80 flex bg-white border border-[#E8E3DD] rounded-full shadow-lg overflow-hidden z-10 focus-within:border-[#6B7A6A]"
                    >
                        <input
                            ref={searchInputRef}
                            type="text"
                            placeholder="Поиск товаров..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    handleSearchSubmit();
                                }
                                if (e.key === 'Escape') setIsSearchOpen(false);
                            }}
                            onBlur={() => setTimeout(() => setIsSearchOpen(false), 200)}
                            className="flex-1 min-w-0 px-4 py-2.5 border-0 text-sm focus:outline-none bg-transparent"
                        />
                        <button
                            type="button"
                            onClick={handleSearchSubmit}
                            aria-label="Найти"
                            className="px-4 text-[#6B7A6A] hover:text-[#1C1917] transition"
                        >
                            <i className="fas fa-search"></i>
                        </button>
                    </div>
                )}

                {/* Мобильное меню */}
                {mobileMenuOpen && (
                    <nav className="md:hidden bg-white border-t border-[#E8E3DD] px-4 py-3 flex flex-col gap-1">
                        {[
                            ['/', 'Каталог'],
                            ['/bestsellers', 'Бестселлеры'],
                            ['/skincare', 'Уход за кожей'],
                            ['/about', 'О нас'],
                            ['/journal', 'Журнал'],
                            ['/account', 'Личный кабинет'],
                            ['/certs', 'Сертификаты'],
                            ['/delivery', 'Оплата и доставка'],
                            ['/privacy', 'Политика ПДн'],
                            ['/offer', 'Оферта'],
                        ].map(([to, label]) => (
                            <NavLink
                                key={to}
                                to={to}
                                end={to === '/'}
                                onClick={() => setMobileMenuOpen(false)}
                                className={({ isActive }) =>
                                    `text-left py-2.5 px-3 rounded-lg text-sm font-medium transition ${isActive ? 'bg-[#F5F0EB] text-[#6B7A6A]' : 'text-[#1C1917] hover:bg-[#FBF9F6]'}`
                                }
                            >
                                {label}
                            </NavLink>
                        ))}
                    </nav>
                )}
            </header>

            <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-8 flex-1">
                {catalogDown && (
                    <div className="mb-6 p-4 rounded-2xl bg-amber-50 border border-amber-200 text-amber-900 flex items-start gap-3">
                        <span className="text-2xl shrink-0">⚙️</span>
                        <div className="flex-1">
                            <p className="font-semibold mb-1">Каталог временно недоступен</p>
                            <p className="text-sm text-amber-800">
                                Мы проводим технические работы. Товары и цены появятся чуть позже — обычно это занимает не больше часа.
                                Корзина, оформленные заказы и чат поддержки работают как обычно.
                            </p>
                            <p className="text-xs text-amber-700 mt-2">
                                Вопросы — <a href="mailto:sale@puresynergy.ru" className="underline">sale@puresynergy.ru</a>{' '}
                                или через <button onClick={openChatBot} className="underline">AI-консультанта</button>.
                            </p>
                        </div>
                    </div>
                )}
                {payResult === 'success' && (
                    <div className="mb-6 p-4 rounded-2xl bg-[#6B7A6A] text-white text-center shadow-lg relative">
                        ✅ Оплата прошла успешно! Мы получили ваш заказ и уже готовим отправление.
                        <button
                            onClick={() => setPayResult(null)}
                            className="absolute top-2 right-3 text-white/80 hover:text-white text-xl leading-none cursor-pointer"
                            aria-label="Закрыть"
                        >
                            ×
                        </button>
                    </div>
                )}
                {payResult === 'fail' && (
                    <div className="mb-6 p-4 rounded-2xl bg-red-500 text-white text-center shadow-lg relative">
                        ⚠️ Платёж не прошёл. Попробуйте ещё раз или выберите другую карту.
                        <button
                            onClick={() => setPayResult(null)}
                            className="absolute top-2 right-3 text-white/80 hover:text-white text-xl leading-none cursor-pointer"
                            aria-label="Закрыть"
                        >
                            ×
                        </button>
                    </div>
                )}
                <Routes>
                    {/* ===== ГЛАВНАЯ ===== */}
                    <Route path="/" element={(
                        <>
                            <section className="py-8 md:py-12">
                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                                    <div>
                                        <p className="text-[#6B7A6A] text-xs tracking-[0.2em] uppercase font-medium mb-4">
                                            клинически доказано • vegan
                                        </p>
                                        <h1 className="serif text-4xl md:text-5xl lg:text-6xl leading-[1.1] text-[#1C1917] mb-6">
                                            Научный уход для сияющей кожи
                                        </h1>
                                        <p className="text-[#4A4A4A] text-base md:text-lg max-w-lg mb-8 leading-relaxed">
                                            Каждая формула строится вокруг короткого списка доказанных активов в концентрациях, которые мы печатаем на упаковке. Без наполнителей, без отдушек, без догадок — просто кожа, которая ведёт себя лучше.
                                        </p>
                                        <div className="flex flex-wrap gap-4 mb-8">
                                            <button onClick={scrollToCatalog} className="btn-primary">Изучить коллекцию</button>
                                            <button onClick={openChatBot} className="btn-outline">Пройти тест кожи</button>
                                        </div>
                                        <hr className="border-[#E8E3DD] my-6" />
                                        <div className="flex flex-wrap gap-x-5 gap-y-2 text-sm text-[#4A4A4A]">
                                            <span className="flex items-center gap-2"><i className="fas fa-certificate text-[#6B7A6A]"></i> Сертифицировано, ЕАС</span>
                                            <span className="flex items-center gap-2"><i className="fas fa-moon text-[#6B7A6A]"></i> Есть сертификаты Халяль</span>
                                            <span className="flex items-center gap-2"><i className="fas fa-flask text-[#6B7A6A]"></i> Концентрации активов на упаковке</span>
                                        </div>
                                    </div>
                                    <div className="relative">
                                        <div className="bg-[#F5F0EB] rounded-3xl overflow-hidden shadow-xl">
                                            <video
                                                key={playlist[currentVideoIndex]}
                                                src={playlist[currentVideoIndex]}
                                                poster="/hero-poster.jpg"
                                                autoPlay
                                                muted
                                                playsInline
                                                onEnded={handleVideoEnd}
                                                className="w-full h-auto object-cover"
                                            />
                                            <div className="absolute bottom-4 left-4 bg-white/80 backdrop-blur-sm rounded-2xl px-4 py-3 shadow-lg border border-white/30">
                                                <p className="serif text-base font-bold">Бестселлер</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </section>
                            <PromoSection promotions={cartPromos} />
                            <section className="pb-8">
                                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                                    {[
                                        { icon: '🚚', title: 'Доставка по РФ 1–3 дня', text: 'СДЭК, Почта, пункты выдачи' },
                                        { icon: '🛡️', title: 'Сертифицированное качество', text: 'Декларации ЕАС, сертификаты Халяль' },
                                        { icon: '👩‍⚕️', title: 'Консультация косметолога', text: 'Бесплатно — в чате на сайте и ВК' },
                                        { icon: '🔒', title: 'Безопасная оплата', text: 'Т-Банк, чек по 54-ФЗ' },
                                    ].map((u, i) => (
                                        <div key={i} className="bg-white rounded-2xl border border-[#E8E3DD] p-5 text-center">
                                            <div className="text-2xl mb-2">{u.icon}</div>
                                            <p className="text-sm font-semibold text-[#1C1917] mb-1">{u.title}</p>
                                            <p className="text-xs text-gray-500">{u.text}</p>
                                        </div>
                                    ))}
                                </div>
                            </section>
                            <div className="pb-8 text-center">
                                <Link to="/certs" className="text-sm font-semibold text-[#6B7A6A] hover:underline">
                                    📄 Качество подтверждено: декларации ЕАС и сертификаты Халяль — смотреть документы →
                                </Link>
                            </div>
                            <section ref={catalogRef} className="py-8 scroll-mt-20" id="catalog-section">
                                <div className="flex items-end justify-between mb-6">
                                    <div>
                                        <p className="text-[#6B7A6A] text-xs tracking-[0.2em] uppercase font-medium">Коллекция</p>
                                        <h2 className="serif text-3xl md:text-4xl text-[#1C1917]">Подбор по задачам</h2>
                                    </div>
                                    <span className="text-sm text-gray-500">{filteredProducts.length} товаров</span>
                                </div>
                                <FilterBar />
                                <div className="mt-8">
                                    <ProductGrid
                                        products={filteredProducts}
                                        addToCart={addToCart}
                                        onProductClick={handleProductClick}
                                        wishlistIds={wishlistIds}
                                        onToggleFav={toggleFav}
                                    />
                                </div>
                            </section>
                            <section className="py-10">
                                <h2 className="serif text-3xl md:text-4xl text-center text-[#1C1917] mb-3">Почему Pure Synergy</h2>
                                <p className="text-center text-gray-500 text-sm mb-8 max-w-2xl mx-auto">
                                    Мы не обещаем «чудо» — мы показываем состав и подтверждаем его протоколами и документами.
                                </p>
                                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                                    {[
                                        { icon: '🧪', title: 'Рабочие концентрации', text: 'TXA 5%, азелоглицин 4%, витамин C 16% — проценты активов указаны на упаковке.' },
                                        { icon: '🧬', title: 'Ламеллярные эмульсии', text: 'Формулы, физиологичные липидному барьеру кожи: восстанавливают, а не «сидят плёнкой».' },
                                        { icon: '🚫', title: 'Без лишнего', text: 'Без отдушек и агрессивных ПАВ. Подходит чувствительной коже, розацеа, при ретинизации.' },
                                        { icon: '📋', title: 'Готовые протоколы', text: 'Схемы ухода по проблемам, составленные косметологами, — раздел «Уход за кожей».' },
                                    ].map((c, i) => (
                                        <div key={i} className="bg-white rounded-2xl border border-[#E8E3DD] p-6">
                                            <div className="text-2xl mb-3">{c.icon}</div>
                                            <p className="font-semibold text-[#1C1917] mb-2">{c.title}</p>
                                            <p className="text-sm text-gray-600 leading-relaxed">{c.text}</p>
                                        </div>
                                    ))}
                                </div>
                            </section>
                        </>
                    )} />

                    {/* ===== БРЕНДЫ И КАТЕГОРИИ ===== */}
                    <Route
                        path="/brand/:brandSlug"
                        element={<CatalogPage products={products} addToCart={addToCart} onProductClick={handleProductClick} wishlistIds={wishlistIds} onToggleFav={toggleFav} />}
                    />
                    <Route
                        path="/category/:categorySlug"
                        element={<CatalogPage products={products} addToCart={addToCart} onProductClick={handleProductClick} wishlistIds={wishlistIds} onToggleFav={toggleFav} />}
                    />
                    <Route
                        path="/brand/:brandSlug/:categorySlug"
                        element={<CatalogPage products={products} addToCart={addToCart} onProductClick={handleProductClick} wishlistIds={wishlistIds} onToggleFav={toggleFav} />}
                    />
                    <Route
                        path="/search"
                        element={<SearchPage
                            products={products}
                            addToCart={addToCart}
                            onProductClick={handleProductClick}
                            wishlistIds={wishlistIds}
                            onToggleFav={toggleFav}
                        />}
                    />
                    {/* ===== БЕСТСЕЛЛЕРЫ — НОВОЕ: реальные товары из BESTSELLER_IDS ===== */}
                    <Route path="/bestsellers" element={(
                        <div className="max-w-6xl mx-auto py-8">
                            <nav className="text-xs text-gray-500 mb-4">
                                <Link to="/" className="hover:text-[#6B7A6A] hover:underline">Каталог</Link>
                                <span className="mx-1">/</span>
                                <span className="text-[#1C1917]">Бестселлеры</span>
                            </nav>

                            <h1 className="serif text-3xl md:text-4xl text-[#1C1917] mb-3">Бестселлеры</h1>
                            <p className="text-gray-600 mb-8 max-w-3xl leading-relaxed">
                                Средства, которые чаще всего покупают и перезаказывают наши клиенты.
                                Живой рейтинг по оплаченным заказам — обновляется автоматически.
                            </p>

                            {bestSellers.length > 0 ? (
                                <ProductGrid
                                    products={bestSellers}
                                    addToCart={addToCart}
                                    onProductClick={handleProductClick}
                                    wishlistIds={wishlistIds}
                                    onToggleFav={toggleFav}
                                />
                            ) : (
                                <div className="text-center py-12">
                                    <p className="text-gray-500 mb-4">Собираем подборку самых популярных товаров…</p>
                                    <Link to="/" className="btn-outline inline-block">Перейти в каталог</Link>
                                </div>
                            )}

                            <div className="mt-16 bg-[#F5F0EB] rounded-2xl p-6 md:p-8 text-center">
                                <p className="serif text-xl mb-3">Не знаете, что выбрать?</p>
                                <p className="text-sm text-gray-600 mb-5 max-w-xl mx-auto">
                                    AI-консультант подберёт уход по типу кожи, задаче и бюджету за 1 минуту.
                                </p>
                                <button onClick={openChatBot} className="btn-primary">
                                    🤖 Спросить AI-консультанта
                                </button>
                            </div>
                        </div>
                    )} />

                    {/* ===== УХОД ЗА КОЖЕЙ ===== */}
                    <Route path="/skincare" element={(
                        <div className="max-w-6xl mx-auto py-12">
                            <h1 className="serif text-4xl md:text-5xl text-[#1C1917] mb-4 text-center">Уход за кожей</h1>
                            <p className="text-gray-600 text-center mb-12 max-w-3xl mx-auto">
                                Индивидуальный подход к каждому типу кожи — основа эффективного ухода.
                                Определите свой тип и следуйте рекомендациям экспертов Pure Synergy.
                            </p>
                            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-16">
                                {[
                                    { type: 'normal', label: 'Нормальная', icon: '✨' },
                                    { type: 'dry', label: 'Сухая', icon: '💧' },
                                    { type: 'oily', label: 'Жирная', icon: '🌟' },
                                    { type: 'combination', label: 'Комбинированная', icon: '⚖️' },
                                    { type: 'sensitive', label: 'Чувствительная', icon: '🌸' },
                                ].map((item) => (
                                    <button
                                        key={item.type}
                                        onClick={() => document.getElementById(item.type)?.scrollIntoView({ behavior: 'smooth' })}
                                        className="p-4 bg-white rounded-2xl border border-[#E8E3DD] hover:border-[#6B7A6A] hover:shadow-lg transition-all text-center"
                                    >
                                        <div className="text-3xl mb-2">{item.icon}</div>
                                        <div className="text-sm font-medium">{item.label}</div>
                                    </button>
                                ))}
                            </div>
                            <section id="normal" className="mb-20 scroll-mt-20">
                                <div className="bg-gradient-to-br from-[#FBF9F6] to-white rounded-3xl p-8 md:p-12 border border-[#E8E3DD]">
                                    <h2 className="serif text-3xl text-[#1C1917] mb-6">Нормальная кожа</h2>
                                    <p className="text-gray-700 mb-6 leading-relaxed">
                                        Сбалансированная кожа с хорошим увлажнением, мелкими порами и ровным тоном.
                                        Требует поддержания естественного баланса и профилактики старения.
                                    </p>
                                    <div className="grid md:grid-cols-2 gap-8 mb-8">
                                        <div className="bg-white rounded-2xl p-6 border border-[#E8E3DD]">
                                            <h3 className="font-semibold text-[#6B7A6A] mb-4">🌅 Утренний уход</h3>
                                            <ul className="space-y-3 text-sm">
                                                <li><strong>1.</strong> Очищение: N.M.F. Hydrating Foam или Corneotherapy Foam</li>
                                                <li><strong>2.</strong> Тонизация: N.M.F. Hydrating Toner</li>
                                                <li><strong>3.</strong> Сыворотка: Vitamin C + TRX Serum (антиоксидантная защита)</li>
                                                <li><strong>4.</strong> Увлажнение: N.M.F. Hydrating Cream-Serum</li>
                                                <li><strong>5.</strong> Защита: Lamellar Cream SPF 30 или 50</li>
                                            </ul>
                                        </div>
                                        <div className="bg-white rounded-2xl p-6 border border-[#E8E3DD]">
                                            <h3 className="font-semibold text-[#6B7A6A] mb-4">🌙 Вечерний уход</h3>
                                            <ul className="space-y-3 text-sm">
                                                <li><strong>1.</strong> Демакияж: Lipid-Care Cleansing Milk</li>
                                                <li><strong>2.</strong> Очищение: Corneotherapy Foam</li>
                                                <li><strong>3.</strong> Тонизация: Corneotherapy Toner</li>
                                                <li><strong>4.</strong> Уход: 3-Lipid Lamellar Cream (восстановление барьера)</li>
                                            </ul>
                                        </div>
                                    </div>
                                    <div className="bg-white/60 rounded-2xl p-6 border-l-4 border-[#6B7A6A]">
                                        <h4 className="font-semibold mb-3">💡 Рекомендации экспертов:</h4>
                                        <ul className="space-y-2 text-sm text-gray-700">
                                            <li>• 1-2 раза в неделю используйте пилинг N.M.F. Hydrating Peel для обновления</li>
                                            <li>• Летом усильте антиоксидантную защиту с Vitamin C+ Cream</li>
                                            <li>• Зимой добавьте интенсивное увлажнение с Laminaria Repairing Cream-Mask</li>
                                            <li>• Для профилактики старения включите в уход DMAE Lifting Serum (после 30 лет)</li>
                                        </ul>
                                    </div>
                                </div>
                            </section>
                            <section id="dry" className="mb-20 scroll-mt-20">
                                <div className="bg-gradient-to-br from-blue-50 to-white rounded-3xl p-8 md:p-12 border border-[#E8E3DD]">
                                    <h2 className="serif text-3xl text-[#1C1917] mb-6">Сухая кожа</h2>
                                    <p className="text-gray-700 mb-6 leading-relaxed">
                                        Кожа с нарушенным гидролипидным барьером, склонная к шелушению, стянутости
                                        и преждевременному старению. Требует интенсивного увлажнения и восстановления барьера.
                                    </p>
                                    <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-8">
                                        <h3 className="font-semibold text-amber-900 mb-3">⚠️ Признаки нарушенного эпидермального барьера:</h3>
                                        <ul className="grid md:grid-cols-2 gap-2 text-sm text-amber-800">
                                            <li>• Чувство стянутости после умывания</li>
                                            <li>• Видимое шелушение</li>
                                            <li>• Тусклый цвет лица</li>
                                            <li>• Повышенная чувствительность</li>
                                            <li>• Мелкие морщинки обезвоживания</li>
                                            <li>• Жжение при нанесении средств</li>
                                        </ul>
                                    </div>
                                    <div className="grid md:grid-cols-2 gap-8 mb-8">
                                        <div className="bg-white rounded-2xl p-6 border border-[#E8E3DD]">
                                            <h3 className="font-semibold text-[#6B7A6A] mb-4">🌅 Утренний уход</h3>
                                            <ul className="space-y-3 text-sm">
                                                <li><strong>1.</strong> Мягкое очищение: <em>Lipid-Care Cleansing Milk</em></li>
                                                <li><strong>2.</strong> Увлажняющий тонер: <em>N.M.F. Hydrating Toner</em></li>
                                                <li><strong>3.</strong> Интенсивное увлажнение: <em>N.M.F. Hydrating Cream-Serum</em></li>
                                                <li><strong>4.</strong> Восстановление барьера: <em>3-Lipid Lamellar Cream</em></li>
                                                <li><strong>5.</strong> SPF-защита: <em>Lamellar Cream SPF 50</em></li>
                                            </ul>
                                        </div>
                                        <div className="bg-white rounded-2xl p-6 border border-[#E8E3DD]">
                                            <h3 className="font-semibold text-[#6B7A6A] mb-4">🌙 Вечерний уход</h3>
                                            <ul className="space-y-3 text-sm">
                                                <li><strong>1.</strong> Демакияж: <em>Lipid-Care Cleansing Milk</em></li>
                                                <li><strong>2.</strong> Очищение: <em>Corneotherapy Foam</em></li>
                                                <li><strong>3.</strong> Восстановление: <em>Corneotherapy Toner</em></li>
                                                <li><strong>4.</strong> Питание: <em>Lipid-Care Calming Cream</em></li>
                                                <li><strong>5.</strong> Маска 2-3 раза в неделю: <em>Laminaria Repairing Cream-Mask</em></li>
                                            </ul>
                                        </div>
                                    </div>
                                    <div className="bg-white/60 rounded-2xl p-6 border-l-4 border-blue-500">
                                        <h4 className="font-semibold mb-3">💧 Ключевые компоненты для сухой кожи:</h4>
                                        <div className="grid md:grid-cols-3 gap-4 text-sm">
                                            <div>
                                                <strong className="text-[#6B7A6A]">Церамиды</strong>
                                                <p className="text-gray-600 mt-1">Восстанавливают липидный барьер (3-Lipid Lamellar Cream)</p>
                                            </div>
                                            <div>
                                                <strong className="text-[#6B7A6A]">N.M.F. комплекс</strong>
                                                <p className="text-gray-600 mt-1">Удерживают влагу в коже (N.M.F. линия)</p>
                                            </div>
                                            <div>
                                                <strong className="text-[#6B7A6A]">Омега 3,6,9</strong>
                                                <p className="text-gray-600 mt-1">Укрепляют защитные функции (2% в 3-Lipid Cream)</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </section>
                            <section id="oily" className="mb-20 scroll-mt-20">
                                <div className="bg-gradient-to-br from-green-50 to-white rounded-3xl p-8 md:p-12 border border-[#E8E3DD]">
                                    <h2 className="serif text-3xl text-[#1C1917] mb-6">Жирная и проблемная кожа</h2>
                                    <p className="text-gray-700 mb-6 leading-relaxed">
                                        Кожа с повышенной выработкой себума, расширенными порами, склонная к комедонам
                                        и воспалениям. Требует нормализации себорегуляции и противовоспалительного ухода.
                                    </p>
                                    <div className="grid md:grid-cols-2 gap-8 mb-8">
                                        <div className="bg-white rounded-2xl p-6 border border-[#E8E3DD]">
                                            <h3 className="font-semibold text-[#6B7A6A] mb-4">🌅 Утренний уход</h3>
                                            <ul className="space-y-3 text-sm">
                                                <li><strong>1.</strong> Очищение: <em>Salicylic Cleansing Gel</em> (контроль себума)</li>
                                                <li><strong>2.</strong> Восстановление микробиома: <em>Microbiome Balance Toner</em></li>
                                                <li><strong>3.</strong> Себорегуляция: <em>Salicylic+ Serum</em></li>
                                                <li><strong>4.</strong> Легкое увлажнение: <em>N.M.F. Hydrating Cream-Serum</em></li>
                                                <li><strong>5.</strong> SPF: <em>Lamellar Cream SPF 30</em> (некомедогенный)</li>
                                            </ul>
                                        </div>
                                        <div className="bg-white rounded-2xl p-6 border border-[#E8E3DD]">
                                            <h3 className="font-semibold text-[#6B7A6A] mb-4">🌙 Вечерний уход</h3>
                                            <ul className="space-y-3 text-sm">
                                                <li><strong>1.</strong> Очищение: <em>Salicylic Cleansing Gel</em></li>
                                                <li><strong>2.</strong> Тонизация: <em>Microbiome Balance Toner</em></li>
                                                <li><strong>3.</strong> Лечение: <em>Salicylic+ Serum</em> (2% салициловая кислота)</li>
                                                <li><strong>4.</strong> Восстановление: <em>3-Lipid Lamellar Cream</em></li>
                                                <li><strong>5.</strong> 1-2 раза в неделю: <em>Azelaic 2-Function Peel</em></li>
                                            </ul>
                                        </div>
                                    </div>
                                    <div className="bg-white/60 rounded-2xl p-6 border-l-4 border-green-600">
                                        <h4 className="font-semibold mb-3">🎯 Активные компоненты против акне:</h4>
                                        <div className="grid md:grid-cols-2 gap-4 text-sm">
                                            <div>
                                                <strong className="text-[#6B7A6A]">Салициловая кислота 2%</strong>
                                                <p className="text-gray-600 mt-1">Открывает комедоны, противовоспалительное действие</p>
                                            </div>
                                            <div>
                                                <strong className="text-[#6B7A6A]">Азелоглицин 4%</strong>
                                                <p className="text-gray-600 mt-1">Блокирует 5-альфа-редуктазу, уменьшает себум</p>
                                            </div>
                                            <div>
                                                <strong className="text-[#6B7A6A]">Цинк PCA</strong>
                                                <p className="text-gray-600 mt-1">Нормализует обновление в протоках сальных желез</p>
                                            </div>
                                            <div>
                                                <strong className="text-[#6B7A6A]">Ниацинамид</strong>
                                                <p className="text-gray-600 mt-1">Укрепляет барьер, уменьшает воспаление</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </section>
                            <section id="combination" className="mb-20 scroll-mt-20">
                                <div className="bg-gradient-to-br from-purple-50 to-white rounded-3xl p-8 md:p-12 border border-[#E8E3DD]">
                                    <h2 className="serif text-3xl text-[#1C1917] mb-6">Комбинированная кожа</h2>
                                    <p className="text-gray-700 mb-6 leading-relaxed">
                                        Сочетание жирной Т-зоны (лоб, нос, подбородок) с нормальной или сухой кожей
                                        на щеках. Требует сбалансированного ухода и зонирования.
                                    </p>
                                    <div className="grid md:grid-cols-2 gap-8 mb-8">
                                        <div className="bg-white rounded-2xl p-6 border border-[#E8E3DD]">
                                            <h3 className="font-semibold text-[#6B7A6A] mb-4">🌅 Утренний уход</h3>
                                            <ul className="space-y-3 text-sm">
                                                <li><strong>1.</strong> Очищение: <em>N.M.F. Hydrating Foam</em></li>
                                                <li><strong>2.</strong> Тонизация: <em>N.M.F. Hydrating Toner</em></li>
                                                <li><strong>3.</strong> Антиоксиданты: <em>Vitamin C+ Cream</em></li>
                                                <li><strong>4.</strong> На Т-зону: <em>Salicylic+ Serum</em> (точечно)</li>
                                                <li><strong>5.</strong> SPF: <em>Lamellar Cream SPF 30</em></li>
                                            </ul>
                                        </div>
                                        <div className="bg-white rounded-2xl p-6 border border-[#E8E3DD]">
                                            <h3 className="font-semibold text-[#6B7A6A] mb-4">🌙 Вечерний уход</h3>
                                            <ul className="space-y-3 text-sm">
                                                <li><strong>1.</strong> Очищение: <em>Corneotherapy Foam</em></li>
                                                <li><strong>2.</strong> Тонизация: <em>Corneotherapy Toner</em></li>
                                                <li><strong>3.</strong> На всё лицо: <em>N.M.F. Hydrating Cream-Serum</em></li>
                                                <li><strong>4.</strong> На Т-зону: <em>Azelaic 2-Function Cream 2%</em></li>
                                                <li><strong>5.</strong> На сухие зоны: <em>3-Lipid Lamellar Cream</em></li>
                                            </ul>
                                        </div>
                                    </div>
                                    <div className="bg-white/60 rounded-2xl p-6 border-l-4 border-purple-500">
                                        <h4 className="font-semibold mb-3">💡 Стратегия зонирования:</h4>
                                        <ul className="space-y-2 text-sm text-gray-700">
                                            <li>• <strong>Т-зона:</strong> себорегулирующие средства (Salicylic+, Azelaic)</li>
                                            <li>• <strong>Щёки:</strong> увлажняющие и восстанавливающие (N.M.F., 3-Lipid)</li>
                                            <li>• <strong>1-2 раза в неделю:</strong> пилинг N.M.F. Hydrating Peel на всё лицо</li>
                                            <li>• <strong>Маски:</strong> на Т-зону — очищающие, на щёки — увлажняющие</li>
                                        </ul>
                                    </div>
                                </div>
                            </section>
                            <section id="sensitive" className="mb-20 scroll-mt-20">
                                <div className="bg-gradient-to-br from-rose-50 to-white rounded-3xl p-8 md:p-12 border border-[#E8E3DD]">
                                    <h2 className="serif text-3xl text-[#1C1917] mb-6">Чувствительная кожа и розацеа</h2>
                                    <p className="text-gray-700 mb-6 leading-relaxed">
                                        Кожа с повышенной реактивностью, склонная к покраснению, жжению,
                                        зуду и воспалению. Требует максимально деликатного ухода и восстановления барьера.
                                    </p>
                                    <div className="bg-rose-100 border border-rose-300 rounded-2xl p-6 mb-8">
                                        <h3 className="font-semibold text-rose-900 mb-3">⛔ Чего избегать:</h3>
                                        <ul className="grid md:grid-cols-2 gap-2 text-sm text-rose-800">
                                            <li>• Агрессивные ПАВ и сульфаты</li>
                                            <li>• Спирт и ментол</li>
                                            <li>• Отдушки и эфирные масла</li>
                                            <li>• Грубые скрабы</li>
                                            <li>• Горячую воду при умывании</li>
                                            <li>• Кислоты в высоких концентрациях</li>
                                        </ul>
                                    </div>
                                    <div className="grid md:grid-cols-2 gap-8 mb-8">
                                        <div className="bg-white rounded-2xl p-6 border border-[#E8E3DD]">
                                            <h3 className="font-semibold text-[#6B7A6A] mb-4">🌅 Утренний уход</h3>
                                            <ul className="space-y-3 text-sm">
                                                <li><strong>1.</strong> Мягкое очищение: <em>Lipid-Care Cleansing Milk</em></li>
                                                <li><strong>2.</strong> Успокаивающий тонер: <em>Corneotherapy Toner</em></li>
                                                <li><strong>3.</strong> Восстановление: <em>Lipid-Care Calming Serum</em></li>
                                                <li><strong>4.</strong> Увлажнение: <em>Lipid-Care Calming Cream</em></li>
                                                <li><strong>5.</strong> SPF: <em>Lamellar Cream SPF 50</em> (обязательно!)</li>
                                            </ul>
                                        </div>
                                        <div className="bg-white rounded-2xl p-6 border border-[#E8E3DD]">
                                            <h3 className="font-semibold text-[#6B7A6A] mb-4">🌙 Вечерний уход</h3>
                                            <ul className="space-y-3 text-sm">
                                                <li><strong>1.</strong> Демакияж: <em>Lipid-Care Cleansing Milk</em></li>
                                                <li><strong>2.</strong> Очищение: <em>Corneotherapy Foam</em></li>
                                                <li><strong>3.</strong> Тонизация: <em>Corneotherapy Toner</em></li>
                                                <li><strong>4.</strong> Восстановление: <em>Tranexamic Serum</em> (при розацеа)</li>
                                                <li><strong>5.</strong> Питание: <em>3-Lipid Lamellar Cream</em></li>
                                            </ul>
                                        </div>
                                    </div>
                                    <div className="bg-white/60 rounded-2xl p-6 border-l-4 border-rose-500">
                                        <h4 className="font-semibold mb-3">🌿 Ключевые активы для чувствительной кожи:</h4>
                                        <div className="grid md:grid-cols-3 gap-4 text-sm">
                                            <div>
                                                <strong className="text-[#6B7A6A]">Транексамовая кислота 5%</strong>
                                                <p className="text-gray-600 mt-1">Снижает воспаление и эритему (Tranexamic Serum)</p>
                                            </div>
                                            <div>
                                                <strong className="text-[#6B7A6A]">Церамиды</strong>
                                                <p className="text-gray-600 mt-1">Восстанавливают барьер (3-Lipid Cream)</p>
                                            </div>
                                            <div>
                                                <strong className="text-[#6B7A6A]">Босвеллиевые кислоты</strong>
                                                <p className="text-gray-600 mt-1">Укрепляют барьер, уменьшают раздражение</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </section>
                            <section className="bg-gradient-to-br from-[#6B7A6A] to-[#4A5A4A] rounded-3xl p-8 md:p-12 text-white">
                                <h2 className="serif text-3xl mb-8 text-center">Нужна помощь с подбором ухода?</h2>
                                <p className="text-center mb-6 text-lg">
                                    Наш AI-консультант проанализирует ваш тип кожи и подберет идеальную программу
                                </p>
                                <div className="text-center">
                                    <button
                                        onClick={openChatBot}
                                        className="bg-white text-[#6B7A6A] px-8 py-4 rounded-full font-semibold hover:bg-gray-100 transition shadow-lg inline-flex items-center gap-2"
                                    >
                                        <span>🤖</span>
                                        Спросить AI-консультанта
                                    </button>
                                </div>
                            </section>
                        </div>
                    )} />
                    {/* ===== О НАС ===== */}
                    <Route path="/about" element={(
                        <div className="max-w-4xl mx-auto py-12">
                            <h1 className="serif text-4xl text-[#1C1917] mb-6">О компании Pure Synergy</h1>
                            <div className="prose prose-lg text-gray-700 leading-relaxed space-y-6">
                                <p>
                                    <strong>Pure Synergy</strong> — это российский бренд профессиональной космецевтики, созданный на стыке науки и передовых технологий. Мы объединяем лучшие мировые практики в области дерматологии, биотехнологий и искусственного интеллекта, чтобы предложить вам уход, который действительно работает.
                                </p>
                                <p>
                                    Наша философия — <strong>«чистая синергия»</strong>: мы выбираем только доказанные активные компоненты в рабочих концентрациях, исключая отдушки, красители и бесполезные наполнители. Каждая формула проходит клинические испытания и подтверждает свою эффективность.
                                </p>
                                <h2 className="serif text-2xl mt-8">AI-консультант — ваш персональный эксперт</h2>
                                <p>
                                    Мы гордимся тем, что одними из первых в индустрии внедрили <strong>интеллектуального помощника на основе искусственного интеллекта</strong>. Наш бот анализирует ваш запрос, тип кожи, бюджет и даже историю покупок, чтобы предложить идеальное средство. Это не просто чат — это цифровой дерматолог, обученный на тысячах научных статей и реальных клиентских кейсов.
                                </p>
                                <p>
                                    Благодаря интеграции с нейросетями и облачными технологиями, консультант понимает сложные запросы, подбирает продукты по ключевым словам (например, «пептиды», «ретинол», «защита от солнца») и объясняет, почему именно это средство подойдёт вам.
                                </p>
                                <h2 className="serif text-2xl mt-8">Наука + ИИ = результат</h2>
                                <p>
                                    Мы верим, что будущее косметологии — за симбиозом фундаментальной науки и цифровых решений. Каждый наш продукт сопровождается подробным описанием активов, а AI-консультант помогает разобраться в сложных ингредиентах.
                                </p>
                                <p>
                                    Присоединяйтесь к сообществу Pure Synergy в <a href="https://vk.ru/pure_synergy" target="_blank" rel="noopener noreferrer" className="text-[#6B7A6A] hover:underline">ВКонтакте</a>.
                                </p>
                            </div>
                            <div className="mt-10 text-center">
                                <Link to="/certs" className="btn-outline inline-block">📄 Сертификаты и декларации</Link>
                            </div>
                        </div>
                    )} />
                    {/* ===== ЖУРНАЛ (список) ===== */}
                    <Route path="/journal" element={(
                        <div className="max-w-6xl mx-auto py-12">
                            <h1 className="serif text-4xl md:text-5xl text-[#1C1917] mb-4 text-center">Журнал Pure Synergy</h1>
                            <p className="text-gray-600 text-center mb-12 max-w-3xl mx-auto">
                                Научные статьи о косметике, ингредиентах и современных подходах к уходу за кожей
                            </p>
                            <div className="flex flex-wrap gap-3 justify-center mb-12">
                                {['Все статьи', 'Ингредиенты', 'Типы кожи', 'Протоколы', 'Anti-age', 'Акне'].map((cat) => (
                                    <button
                                        key={cat}
                                        className="px-6 py-2 rounded-full border border-[#E8E3DD] hover:border-[#6B7A6A] hover:bg-[#6B7A6A] hover:text-white transition text-sm"
                                    >
                                        {cat}
                                    </button>
                                ))}
                            </div>
                            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                                {articles.map((article) => (
                                    <article key={article.slug} className="bg-white rounded-2xl overflow-hidden border border-[#E8E3DD] hover:shadow-xl transition flex flex-col">
                                        <div className={`h-48 ${article.cover}`}></div>
                                        <div className="p-6 flex flex-col flex-1">
                                            <div className="text-xs text-[#6B7A6A] font-semibold mb-2">{article.category}</div>
                                            <h3 className="serif text-xl font-bold mb-3 text-[#1C1917] flex-1">{article.title}</h3>
                                            <p className="text-sm text-gray-600 mb-4 flex-1">{article.excerpt}</p>
                                            <div className="flex items-center justify-between text-xs text-gray-500 pt-4 border-t border-[#E8E3DD]">
                                                <span>{article.readTime}</span>
                                                <Link to={`/journal/${article.slug}`} className="text-[#6B7A6A] font-semibold hover:underline flex items-center gap-1">
                                                    Читать →
                                                </Link>
                                            </div>
                                        </div>
                                    </article>
                                ))}
                            </div>
                            <div className="mt-16 bg-gradient-to-br from-[#6B7A6A] to-[#4A5A4A] rounded-3xl p-8 md:p-12 text-white text-center">
                                <h2 className="serif text-3xl mb-4">Нужна персональная рекомендация?</h2>
                                <p className="text-gray-200 mb-8 max-w-2xl mx-auto">
                                    Наш AI-консультант проанализирует ваш тип кожи, проблемы и подберёт идеальную программу ухода из продуктов Pure Synergy
                                </p>
                                <button
                                    onClick={openChatBot}
                                    className="bg-white text-[#6B7A6A] px-8 py-4 rounded-full font-semibold hover:bg-gray-100 transition shadow-lg inline-flex items-center gap-2"
                                >
                                    <span>🤖</span>
                                    Задать вопрос AI-консультанту
                                </button>
                            </div>
                        </div>
                    )} />
                    {/* ===== SEO-СТРАНИЦА СТАТЬИ ===== */}
                    <Route path="/journal/:slug" element={<ArticlePage />} />
                    {/* ===== SEO-СТРАНИЦА ТОВАРА ===== */}
                    <Route path="/product/:id" element={<ProductPage products={products} addToCart={addToCart} />} />
                    {/* ===== СЕРТИФИКАТЫ ===== */}
                    <Route path="/certs" element={(
                        <div className="max-w-6xl mx-auto py-12">
                            <h1 className="serif text-4xl md:text-5xl text-[#1C1917] mb-4 text-center">
                                Сертификаты и декларации
                            </h1>
                            <p className="text-gray-600 text-center mb-10 max-w-3xl mx-auto">
                                Вся продукция соответствует ТР ТС 009/2011 «О безопасности парфюмерно-косметической продукции».
                                Ниже — действующие декларации о соответствии и сертификаты Халяль.
                            </p>
                            <CertsSection />
                        </div>
                    )} />
                    {/* ===== ОПЛАТА И ДОСТАВКА ===== */}
                    <Route path="/delivery" element={<DeliveryPage />} />
                    {/* ===== ПРАВОВЫЕ СТРАНИЦЫ ===== */}
                    <Route path="/privacy" element={<PrivacyPage />} />
                    <Route path="/account/login" element={<AuthPage />} />
                    <Route path="/account" element={<AccountPage products={products} addToCart={addToCart} openCart={openCart} />} />
                    <Route path="/account/wishlist" element={<WishlistPage products={products} addToCart={addToCart} wishlistIds={wishlistIds} onToggleFav={toggleFav} />} />
                    <Route path="/offer" element={<OfferPage />} />
                    {/* ===== 404 ===== */}
                    <Route path="*" element={(
                        <div className="text-center py-20">
                            <h1 className="serif text-3xl mb-4">Страница не найдена</h1>
                            <p className="text-gray-500"><Link to="/" className="text-[#6B7A6A] underline">Вернуться в каталог</Link></p>
                        </div>
                    )} />
                </Routes>
            </main>

            <footer className="bg-white border-t border-[#E8E3DD] py-6 mt-auto">
                <div className="container mx-auto px-4 flex flex-col sm:flex-row justify-between items-center gap-4 text-sm text-gray-500">
                    <div className="text-center sm:text-left">
                        <span>© 2026 Pure Synergy. Все права защищены.</span>
                        <span className="block text-xs text-gray-400 mt-1">
                            ИП Кодацкая Е.А. · ОГРНИП 326508100445985 · ИНН 501216827208 · sale@puresynergy.ru
                        </span>
                    </div>
                    <div className="flex items-center gap-6">
                        <Link to="/certs" className="hover:text-[#6B7A6A] transition flex items-center gap-2">
                            <i className="fas fa-certificate text-lg"></i> Сертификаты
                        </Link>
                        <a href="mailto:sale@puresynergy.ru" className="hover:text-[#6B7A6A] transition flex items-center gap-2">
                            <i className="fas fa-envelope text-lg"></i> sale@puresynergy.ru
                        </a>
                        <Link to="/delivery" className="hover:text-[#6B7A6A] transition flex items-center gap-2">
                            <i className="fas fa-truck text-lg"></i> Оплата и доставка
                        </Link>
                        <Link to="/privacy" className="hover:text-[#6B7A6A] transition flex items-center gap-2">
                            <i className="fas fa-shield-alt text-lg"></i> Политика ПДн
                        </Link>
                        <Link to="/offer" className="hover:text-[#6B7A6A] transition flex items-center gap-2">
                            <i className="fas fa-file-contract text-lg"></i> Оферта
                        </Link>
                        <a
                            href="https://vk.ru/pure_synergy"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="hover:text-[#6B7A6A] transition flex items-center gap-2"
                        >
                            <i className="fab fa-vk text-lg"></i> ВКонтакте
                        </a>
                        <button
                            onClick={() => alert('Ссылка на Instagram будет добавлена позже')}
                            className="hover:text-[#6B7A6A] transition flex items-center gap-2 bg-transparent border-0 cursor-pointer"
                        >
                            <i className="fab fa-instagram text-lg"></i> Instagram
                        </button>
                        <button
                            onClick={() => alert('Ссылка на Telegram будет добавлена позже')}
                            className="hover:text-[#6B7A6A] transition flex items-center gap-2 bg-transparent border-0 cursor-pointer"
                        >
                            <i className="fab fa-telegram text-lg"></i> Telegram
                        </button>
                    </div>
                </div>
            </footer>
            <SupportChat />
            <ChatBot
                userId={userId}
                addToCart={addToCart}
                onProductClick={setSelectedProduct}
            />
            <ProductModal
                product={selectedProduct}
                onClose={handleModalClose}
                addToCart={addToCart}
            />
            <CartModal
                isOpen={cartOpen}
                onClose={() => setCartOpen(false)}
                cart={cart}
                removeFromCart={removeFromCart}
                updateQuantity={updateQuantity}
                totalPrice={totalPrice}
                userId={userId}
                metrikaId={metrikaId}
                products={products}
                addToCart={addToCart}
                cartPromos={cartPromos}
                isAuthenticated={isAuthenticated}
                bonusBalance={bonusBalance}
                onBonusUsed={(used) => {
                    // после успешной оплаты локально обнуляем
                    setBonusBalance((b) => Math.max(0, b - used));
                }}
            />
            <ArticleModal
                article={selectedArticle}
                onClose={() => setSelectedArticle(null)}
            />
            <CookieBanner />
        </div>
    );
}

export default function App() {
    return (
        <AuthProvider>
            <SupportProvider>
                <BrowserRouter>
                    <Routes>
                        <Route path="/admin" element={<AdminPage />} />
                        <Route path="*" element={<Shell />} />
                    </Routes>
                </BrowserRouter>
            </SupportProvider>
        </AuthProvider>
    );
}